import _, { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import QCXTransportadorInternacionalForm from '../../../../../components/transportador-internacional/QCXTransportadorInternacionalForm';
import { register, save } from '../../../../../features/transportador-internacional/transportadorInternacionalAPI';
import {
  addToList,
  changeToBackgroundCreateMode,
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  failure,
  fetchByIdAsync,
  loading,
  preparingAction,
  resetBackgroundMode,
  resetModel,
  selectBackgroundMode,
  selectMode,
  selectStatus,
  selectTransportadorInternacional,
  setModel,
  success,
  updateOnList,
} from '../../../../../features/transportador-internacional/transportadorInternacionalSlice';
import QCXRegistrationFormPageTemplate from '../../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
import { normalizeDigits } from '../../../../../utils/general/general-utils';
import { formatCep, formatCnpj } from '../../../../../utils/hooks/form/field/formatters';
import {
  isBackgroundCreateMode,
  isConsultMode,
  isCreateMode,
  isNoneMode,
  isPreparingActionStatus,
  isUpdateMode,
} from '../../../../../utils/store/store-utils';
import { setErrorFeedback, setSuccessFeedback } from '../../../../../features/feedback/feedbackSlice';

export default function TransportadorInternacionalRegistrationPage({ authInfo = {} }) {
  const { t } = useTranslation();

  const { id } = useParams();

  const history = useHistory();

  const dispatch = useDispatch();

  const status = useSelector(selectStatus);
  const mode = useSelector(selectMode);
  const backgroundMode = useSelector(selectBackgroundMode);
  const transportadorInternacional = useSelector(selectTransportadorInternacional);

  const isNone = useMemo(() => isNoneMode(mode), [mode]);

  const isCreate = useMemo(() => isCreateMode(mode), [mode]);

  const isBackgroundCreate = useMemo(() => isBackgroundCreateMode(backgroundMode), [backgroundMode]);

  const isConsult = useMemo(() => isConsultMode(mode), [mode]);

  const isUpdate = useMemo(() => isUpdateMode(mode), [mode]);

  const isPreparingAction = useMemo(() => isPreparingActionStatus(status), [status]);

  const handleChangeToPreparingAction = () => {
    dispatch(preparingAction());
  };

  const handleChangeToCreate = () => {
    dispatch(changeToCreateMode());
  };

  const handleChangeToBackgroundCreate = () => {
    dispatch(changeToBackgroundCreateMode());
  };

  const handleChangeToConsult = () => {
    dispatch(changeToConsultMode());
  };

  const handleChangeToUpdate = () => {
    dispatch(changeToUpdateMode());
  };

  const handleResetBackgroundMode = () => {
    dispatch(resetBackgroundMode());
  };

  useEffect(() => {
    if (!isEmpty(id)) {
      dispatch(fetchByIdAsync(id));
      handleChangeToConsult();
    }
  }, [id]);

  const handleCancelUpdate = () => {
    const currentId = isBackgroundCreate ? transportadorInternacional?.id : id;

    if (currentId) {
      dispatch(fetchByIdAsync(currentId));
    }
    handleChangeToConsult();
  };

  const normalize = useCallback((unnormalizedData) => {
    const normalizedPessoaJuridica = {
      ...unnormalizedData?.pessoaJuridica,
      cnpj: normalizeDigits(unnormalizedData?.pessoaJuridica?.cnpj),
    };

    const awbRangesNormalized = (_.isArray(unnormalizedData?.awbRanges)
      ? unnormalizedData.awbRanges
      : []
    )
      .filter((r) => r && (!_.isEmpty(r.inicioAwb) || !_.isEmpty(r.finalAwb)))
      .map((r) => ({
        id: _.isNumber(r.id) ? r.id : null,
        inicioAwb: r.inicioAwb ?? '',
        finalAwb: r.finalAwb ?? '',
      }));

    const normalizedData = {
      ...unnormalizedData,

      ignorableFields: undefined,

      freeTime: _.isEmpty(unnormalizedData?.freeTime) ? null : unnormalizedData.freeTime,

      awbRanges: awbRangesNormalized,

      pessoaJuridica: normalizedPessoaJuridica,
      description: unnormalizedData?.description ?? normalizedPessoaJuridica?.nome,

      endereco: {
        ...unnormalizedData?.endereco,
        cidade: unnormalizedData?.endereco?.cidade?.id ? unnormalizedData?.endereco?.cidade : null,
        cep: normalizeDigits(unnormalizedData?.endereco?.cep),
      },
    };

    // ✅ remove a chave de fato (não só undefined), se você preferir:
    delete normalizedData.ignorableFields;

    return normalizedData;
  }, []);


  const unnormalize = useCallback((normalizedData) => {
    const normalizedPessoaJuridica = {
      ...normalizedData?.pessoaJuridica,
      cnpj: formatCnpj(normalizedData?.pessoaJuridica?.cnpj),
    };

    const awbRangesUnnormalized = _.isArray(normalizedData?.awbRanges)
      ? normalizedData.awbRanges.map((r) => ({
        id: r?.id ?? null, // vem do banco como number
        inicioAwb: r?.inicioAwb ?? '',
        finalAwb: r?.finalAwb ?? '',
      }))
      : [];

    const unnormalizedData = {
      ...normalizedData,
      freeTime: normalizedData?.freeTime ?? '',

      awbRanges: awbRangesUnnormalized,

      pessoaJuridica: normalizedPessoaJuridica,
      endereco: {
        ...normalizedData?.endereco,
        cep: formatCep(normalizedData?.endereco?.cep),
      },
    };

    return unnormalizedData;
  }, []);


  const handleDispatchSetModel = useCallback(
    (data) => {
      dispatch(setModel(normalize(data)));
    },
    [normalize]
  );

  const create = async (data) => {
    const executeDebounced = debounce(async () => {
      try {
        const response = await register(data);

        if (response?.status === 201) {
          dispatch(resetModel());

          const created = response?.data;

          handleDispatchSetModel(created);
          dispatch(addToList({ data: created }));

          const handleResultWithDebounce = debounce(() => {
            history.push(t(t('com.muralis.qcx.url.moduloOperacionaisTransportadorInternacional')));

            dispatch(success());
            dispatch(
              setSuccessFeedback({
                message: t('com.muralis.qcx.mensagem.transportadorInternacionalCadastradoMensagem', {
                  empresa: data?.pessoaJuridica?.nome,
                }),
              })
            );
          }, 500);

          handleResultWithDebounce();
        }
      } catch (error) {
        dispatch(failure());
        let errorMessage = t('com.muralis.qcx.erro.erroTransportadorInternacionalSalvar');
        if (error?.response && error?.response?.data) {
          errorMessage = t('com.muralis.qcx.erro.erroTransportadorInternacionalSalvarMensagem', {
            erro: error?.response.data.message,
          });
        }
        dispatch(
          setErrorFeedback({
            message: errorMessage,
          })
        );
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const update = async (data) => {
    const executeDebounced = debounce(async () => {
      try {
        const response = await save(data);

        if (response?.status === 200) {
          const handleResultWithDebounce = debounce(() => {
            handleChangeToConsult();
            dispatch(success());

            const saved = response?.data;

            dispatch(
              setSuccessFeedback({
                status: response.status,
                data: saved,
                message: t('com.muralis.qcx.mensagem.transportadorInternacionalSalvo', {
                  transportador: saved?.pessoaJuridica?.nome,
                }),
              })
            );

            dispatch(setModel(saved));
            dispatch(updateOnList({ data: saved }));
            dispatch(fetchByIdAsync(saved?.id));
          }, 500);

          handleResultWithDebounce();
          handleChangeToConsult();
        }
      } catch ({ response }) {
        dispatch(failure());
        dispatch(
          setErrorFeedback({
            message: t('com.muralis.qcx.erro.erroTransportadorInternacionalSalvarEspecifico', {
              transportador: data?.pessoaJuridica?.nome,
              erro: response?.data?.message,
            }),
          })
        );
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const handleSubmit = async (data) => {
    const normalizedData = normalize(data);

    if (isUpdate && !isBackgroundCreate) {
      await update(normalizedData);
    } else {
      await create(normalizedData);
    }
  };

  const model = useMemo(
    () => (!isCreate ? unnormalize(transportadorInternacional) : {}),
    [isCreate, transportadorInternacional, unnormalize]
  );

  const actionName = useMemo(() => {
    if (isCreate || isBackgroundCreate || isNone) return t('com.muralis.qcx.acoes.novo');
    if (isConsult) return t('com.muralis.qcx.acoes.visualizar');
    return t('com.muralis.qcx.acoes.alterar');
  }, [isNone, isCreate, isConsult, isBackgroundCreate]);

  const breadcrumbs = useMemo(
    () => [
      {
        link: {
          to: '/',
          name: t('com.muralis.qcx.inicio'),
        },
      },
      {
        text: {
          name: t('com.muralis.qcx.cadastros'),
        },
      },
      {
        link: {
          to: t('com.muralis.qcx.url.moduloOperacionais'),
          name: t('com.muralis.qcx.operacionais'),
        },
      },
      {
        link: {
          to: t(t('com.muralis.qcx.url.moduloOperacionaisTransportadorInternacional')),
          name: t('com.muralis.qcx.transportadorInternacional.label'),
        },
      },
      {
        text: {
          name: actionName,
        },
      },
    ],
    [actionName]
  );

  const pageTitle = useMemo(
    () =>
      isNone || isCreate || isBackgroundCreate
        ? t('com.muralis.qcx.transportadorInternacional.novoTransportadorInternacional')
        : t('com.muralis.qcx.transportadorInternacional.novoTransportadorExistente', {
          ID: transportadorInternacional?.description || transportadorInternacional?.id || '-',
        }),
    [isNone, isCreate, isBackgroundCreate, transportadorInternacional]
  );

  return (
    <QCXRegistrationFormPageTemplate
      pageTitle={pageTitle}
      breadcrumbs={breadcrumbs}
      isCreate={isCreate}
      isConsult={isConsult}
      isUpdate={isUpdate}
      isBackgroundCreate={isBackgroundCreate}
      isPreparingAction={isPreparingAction}
      handleChangeToPreparingAction={handleChangeToPreparingAction}
      handleChangeToCreate={handleChangeToCreate}
      handleChangeToBackgroundCreate={handleChangeToBackgroundCreate}
      handleChangeToConsult={handleChangeToConsult}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      handleResetBackgroundMode={handleResetBackgroundMode}
      authInfo={authInfo}
    >
      {(formProps) => (
        <QCXTransportadorInternacionalForm
          initialValues={model}
          handleChangeModel={handleDispatchSetModel}
          handleSubmit={handleSubmit}
          authInfo={authInfo}
          requiredRoles={['transportador-internacional']}
          {...formProps}
        />
      )}
    </QCXRegistrationFormPageTemplate>
  );
}
